import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { KeyboardShortcutsHelpComponent } from '../components/keyboard-shortcuts-help/keyboard-shortcuts-help.component';
import { PreviewFeaturesComponent } from '../components/preview-features/preview-features.component';
import { AppService } from './app.service';
import { EmbedService } from './embed.service';
import { PortalBridgeService, PortalTopBarAction } from './portal-bridge.service';

/**
 * Lends this app's chrome to the Edara Portal while embedded: the colour its header should take,
 * and the top-bar actions that have no portal equivalent.
 *
 * Both are PUBLISHED rather than configured in the portal, so the portal carries no knowledge of
 * this app. It shows whatever arrives and disappears again when the frame closes — nothing is
 * hard-coded per product, and a change here needs no portal release.
 *
 * The colour is read from the live computed style rather than hard-coded, so a light/dark switch
 * repaints the portal header with the app in one step and the two can never disagree.
 *
 * Inert unless embedded.
 */
@Injectable({ providedIn: 'root' })
export class PortalChromeService {

  private started = false;
  private lastPublished = '';

  constructor(
    private embedService: EmbedService,
    private bridge: PortalBridgeService,
    private appService: AppService,
    private translateService: TranslateService,
    private router: Router,
    private dialog: MatDialog
  ) { }

  /** Begins publishing chrome. Safe to call unconditionally and more than once. */
  public start(): void {
    if (!this.embedService.isEmbedded || this.started) return;
    this.started = true;

    // Theme changes swap the colour; language changes retranslate the action labels.
    //
    // Both are deferred a tick, because the colours are read from the LIVE computed style and the
    // state change lands before the DOM does: AppService.changeTheme() emits on appState$ first
    // and only then swaps the `light-theme` / `dark-theme` classes on <body>. Publishing
    // synchronously would therefore send the OUTGOING theme's colours every time, leaving the
    // portal header permanently one toggle behind the app.
    this.appService.appState$.subscribe(() => this.publishSoon());
    this.translateService.onLangChange.subscribe(() => {
      this.lastPublished = '';
      this.publishSoon();
    });

    this.publish();
  }

  /**
   * Runs a top-bar action the user triggered from the portal header. Anything unrecognised is
   * ignored rather than assumed — the portal only ever echoes ids this app published, but it is
   * a separate window and must not be trusted to.
   */
  public runAction(action: string): void {
    switch (action) {
      case 'keyboard-shortcuts':
        this.dialog.open(KeyboardShortcutsHelpComponent, {
          width: '950px', height: '95vh', autoFocus: false, restoreFocus: false, closeOnNavigation: true
        });
        break;
      case 'set-pass-code':
        this.router.navigate(['/set-pass-code']);
        break;
      case 'preview-features':
        this.dialog.open(PreviewFeaturesComponent, {
          width: '400px', height: '95vh', autoFocus: false, restoreFocus: false, closeOnNavigation: true,
          direction: this.translateService.instant('uiDirection')
        });
        break;
    }
  }

  /** Publishes after the current task, so DOM changes made alongside the trigger have landed. */
  private publishSoon(): void {
    setTimeout(() => this.publish());
  }

  private publish(): void {
    const payload = {
      background: this.headerBackground(),
      color: this.readVar('--m3-sys-on-surface'),
      actions: this.actions()
    };

    const serialized = JSON.stringify(payload);
    if (serialized === this.lastPublished) return;
    this.lastPublished = serialized;

    this.bridge.send({ type: 'chrome', ...payload });
  }

  /**
   * The app's own page background, as a CSS `background` shorthand the portal can apply verbatim
   * to its header so the two surfaces match exactly.
   *
   * It is not a flat colour: `%sys-bg` paints a translucent surface tint over the background
   * colour, so reproducing it means sending the same two layers rather than one resolved value —
   * a single averaged colour would drift from the app in one theme or the other.
   */
  private headerBackground(): string {
    const background = this.readVar('--m3-sys-background');
    const tint = this.readVar('--color-surface-1');
    if (!background) return '';
    return tint ? `linear-gradient(0deg, ${tint}, ${tint}), ${background}` : background;
  }

  /**
   * The top-bar entries that have no portal equivalent and would otherwise be unreachable, since
   * the portal's header replaces this app's. Deliberately NOT the whole old toolbar: the profile,
   * organization switcher, language and theme all exist in the portal header already.
   */
  private actions(): PortalTopBarAction[] {
    return [
      { id: 'keyboard-shortcuts', icon: 'help', label: this.translateService.instant('topBar.help.keyboardShortcuts') },
      { id: 'set-pass-code', icon: 'lock', label: this.translateService.instant('topBar.menu.passCode') },
      { id: 'preview-features', icon: 'star', label: this.translateService.instant('topBar.menu.previewFeatures') }
    ];
  }

  /** Resolved value of a CSS custom property on the document body, or '' when absent. */
  private readVar(name: string): string {
    return getComputedStyle(document.body).getPropertyValue(name).trim();
  }
}
